import PropTypes from 'prop-types';
import React, {
  useCallback,
  useMemo,
  useEffect,
  useState,
  useRef,
} from 'react';
import { get, size } from 'lodash';
import styled from 'styled-components';
import { createEditor, Node, Transforms } from 'slate';
import { Slate, withReact, ReactEditor } from 'slate-react';
import { withHistory } from 'slate-history';
import {
  BoldPlugin,
  EditablePlugins,
  ItalicPlugin,
  UnderlinePlugin,
  StrikethroughPlugin,
  pipe,
  serializeHTMLFromNodes,
  deserializeHTMLToDocument,
  withInlineVoid,
  ImagePlugin,
  TablePlugin,
  ExitBreakPlugin,
  SoftBreakPlugin,
  LinkPlugin,
  MentionPlugin,
} from '@udecode/slate-plugins';
import { useMention } from './useMention';

import { Toolbar, SuggestionsToolbar, TOOLBAR_ITEMS } from './components';
import { CustomMentionSelect } from './components/CustomMentionSelect';
import { MentionElement } from './components/MentionElement';
import getHtmlDocument from '../../utils/getHtmlDocument';
import htmlDecode from '../../utils/htmlDecode';
import isHtml from '../../utils/isHtml';
import { options, serializableOptions } from './lib/config';
import { linkifyPlugin, withLink } from './lib/linkify';
import { paragraphPlugin } from './lib/paragraph';
import { plainTextInDivPlugin } from './lib/div';
import { normalizeHTMLString } from '../../store/sagas/utils/utils';

// TODO: update slate when pr will merged
// HACK: fixing Slate react auto scroll problem
// About problem: https://github.com/ianstormtaylor/slate/pull/3746
const { toDOMRange } = ReactEditor;

ReactEditor.toDOMRange = (editor, selection) => {
  const range = toDOMRange(editor, selection);
  if (range.startContainer.parentElement) {
    range.startContainer.parentElement.getBoundingClientRect =
      range.getBoundingClientRect.bind(range);
  }
  return range;
};

const createPlugins = (pluginOptions, plain, isMentionsEnabled) => {
  const basePlugins = [
    BoldPlugin(),
    ItalicPlugin(),
    UnderlinePlugin(),
    StrikethroughPlugin(),
    ImagePlugin(),
    TablePlugin(),
    ExitBreakPlugin(),
    LinkPlugin(),
    linkifyPlugin(pluginOptions),
    paragraphPlugin(pluginOptions),
    plainTextInDivPlugin(pluginOptions),
  ];

  if (isMentionsEnabled) {
    basePlugins.push(
      MentionPlugin({
        mention: {
          type: 'mention',
          component: MentionElement,
        },
      })
    );
  }

  if (plain) {
    basePlugins.push(
      SoftBreakPlugin({
        rules: [{ hotkey: 'shift+enter' }, { hotkey: 'enter' }],
      })
    );
  }

  return basePlugins;
};

const Editor = props => {
  const {
    className,
    placeholder,
    outerValue,
    toolbarMarks,
    toolbarButtons,
    onChange,
    onFocus,
    onBlur,
    editorId,
    disableToolbar,
    plain,
    suggestions,
    managers,
    onApplySuggestion,
    onAddMention: outerAddMention,
  } = props;

  const isMentionsEnabled = !!managers?.length;

  const plugins = useMemo(
    () => createPlugins(options, plain, isMentionsEnabled),
    [options, plain]
  );
  const serializablePlugins = useMemo(
    () => createPlugins(serializableOptions, plain, isMentionsEnabled),
    [serializableOptions, plain]
  );
  const withPlugins = [
    withReact,
    withHistory,
    withLink(options),
    withInlineVoid({ plugins }),
  ];

  const serializePlaintextFromNodes = nodes =>
    nodes.map(n => Node.string(n)).join('\n');

  const deserialize = useMemo(
    () => deserializeHTMLToDocument(plugins),
    [plugins]
  );
  const serialize = useMemo(
    () =>
      plain
        ? serializePlaintextFromNodes
        : serializeHTMLFromNodes(serializablePlugins),
    [serializablePlugins, plain]
  );

  const getValue = useCallback(
    text => {
      const str = (!plain && normalizeHTMLString(text)) || text;

      if (str.trim() && isHtml(htmlDecode(str))) {
        return deserialize(getHtmlDocument(htmlDecode(str)).body);
      }

      return deserialize(getHtmlDocument(str).body);
    },
    [plain, deserialize]
  );

  const initialValue = useMemo(() => getValue(outerValue), [outerValue]);
  const [value, setValue] = useState(initialValue);

  const [suggestion, setSuggestion] = useState(null);
  useEffect(() => {
    setSuggestion(
      !!suggestions?.length ? getValue(suggestions[0]?.text) : null
    );
  }, [suggestions]);

  const editor = useMemo(() => pipe(createEditor(), ...withPlugins), []);

  const {
    target,
    values,
    index,
    onChangeMention,
    onAddMention,
    onKeyDownMention,
  } = useMention(
    editor,
    (managers || []).map(manager => ({
      value: manager.name,
      data: { ...manager },
    })),
    { trigger: '@' },
    outerAddMention
  );

  editor.id = editorId;
  const editorRef = useRef();

  const handleChange = useCallback(
    newValue => {
      setValue(newValue);
      onChange(serialize(newValue), !suggestion);
      onChangeMention();
    },
    [suggestion, onChange]
  );

  const handleApplySuggestion = useCallback(
    suggest => {
      setSuggestion(null);
      handleChange(getValue(suggest?.text));
      onApplySuggestion(suggest);
    },
    [onApplySuggestion]
  );

  const handleChangeSuggestion = useCallback(({ text }) => {
    setSuggestion(getValue(text));
  }, []);

  useEffect(() => {
    if (outerValue !== serialize(value)) {
      Transforms.deselect(editor);
      editor.children = getValue(outerValue);
      setValue(getValue(outerValue));
    }
  }, [outerValue]);

  useEffect(() => {
    if (plain) {
      const valueChildrens = get(value, '[0].children');
      if (size(valueChildrens) > 1) Transforms.mergeNodes(editor);
    }
  }, [plain, value]);

  // ref для актуальной функции onKeyDownMention, т.к. EditablePlugins хранит старую ссылку на хендлер
  const onKeyDownMentionRef = useRef(onKeyDownMention);

  // синхронизируем ref с последней версией обработчика
  useEffect(() => {
    onKeyDownMentionRef.current = onKeyDownMention;
  }, [onKeyDownMention]);

  const editableOnKeyDown = useCallback(
    e => {
      const handled = onKeyDownMentionRef.current
        ? onKeyDownMentionRef.current(e, editor)
        : undefined;

      if (handled === false) e.preventDefault();
    },
    [editor]
  );

  return (
    <StyledEditor id={editorId} className={className} {...props}>
      <Slate
        ref={editorRef}
        value={suggestion || value}
        editor={editor}
        onChange={handleChange}
      >
        {!disableToolbar && (
          <Toolbar marks={toolbarMarks} buttons={toolbarButtons} />
        )}
        <StyledEditorSuggestions active={!!suggestion}>
          <EditablePlugins
            onFocus={onFocus}
            onBlur={onBlur}
            plugins={plugins}
            placeholder={placeholder}
            spellCheck
            readOnly={!!suggestion}
            onKeyDown={[editableOnKeyDown]}
          />
          {isMentionsEnabled && target && (
            <CustomMentionSelect
              editor={editor}
              at={target}
              options={values}
              valueIndex={index}
              onClickMention={onAddMention}
            />
          )}
          {!!suggestion && (
            <SuggestionsToolbar
              suggestions={suggestions}
              onApply={handleApplySuggestion}
              onChange={handleChangeSuggestion}
            />
          )}
        </StyledEditorSuggestions>
      </Slate>
    </StyledEditor>
  );
};

Editor.propTypes = {
  editorId: PropTypes.string.isRequired,
  className: PropTypes.string,
  outerValue: PropTypes.string,
  placeholder: PropTypes.string,
  onChange: PropTypes.func,
  onApplySuggestion: PropTypes.func,
  onFocus: PropTypes.func,
  onBlur: PropTypes.func,
  disableToolbar: PropTypes.bool,
  toolbarMarks: PropTypes.arrayOf(PropTypes.string),
  toolbarButtons: PropTypes.arrayOf(PropTypes.element),
  plain: PropTypes.bool,
};

Editor.defaultProps = {
  className: '',
  outerValue: '',
  placeholder: '',
  toolbarMarks: [
    TOOLBAR_ITEMS.BOLD,
    TOOLBAR_ITEMS.ITALIC,
    TOOLBAR_ITEMS.UNDERLINE,
    TOOLBAR_ITEMS.STRIKETHROUGH,
  ],
  toolbarButtons: [],
  onChange: Function.prototype,
  onFocus: Function.prototype,
  onBlur: Function.prototype,
  onApplySuggestion: Function.prototype,
  disableToolbar: false,
  plain: false,
};

const StyledEditor = styled.div`
  position: relative;
  width: 100%;
  min-width: 300px;
  max-width: 960px;
  background: white;
  font-family: 'Backpack', sans-serif;
  font-size: 14px;
  line-height: 1.36;
`;

const StyledEditorSuggestions = styled.div`
  padding: 5px;
  border-radius: 10px 10px 0px;

  span {
    font-size: 14px;
  }

  span {
    font-size: 14px;
  }

  ${(props) =>
    props.active &&
    `
    height: 160px;
    overflow: auto;
    background-image: linear-gradient(180deg, #ADD1F8 0%, #DBECFF 100%);

    > div {
      -ms-overflow-style: none;
      scrollbar-width: none;

      ::-webkit-scrollbar {
        width: 0px;
        background: transparent;
      }
    }
  `}
`;

export default Editor;
